import { memo } from 'react';

import styles from './Medallions.module.scss';

/**
 * The freeze medallion (canvas: Freeze), the flame's sibling: the same dark
 * disc, an ice crystal with bevelled arms and a glossy hex core. Sizes follow
 * one rule across the feature (EN-11236): 72 done state, 40 ticket or row
 * header, 30 a row inside a card, 24 the banner stub (20 in the header).
 *
 * Still by design; see Medallions.module.scss for why the canvas glint loop
 * is not shipped.
 */
export type FreezeProps = { size: number; dim?: boolean };

function Freeze({ size, dim }: FreezeProps) {
  // Under 28 px (the banner stub) there is no halo and the shadow is tight,
  // so the rings are the medallion's true edge.
  const compact = size < 28;
  const shadow =
    'inset 0 1px 0 rgba(220,240,255,0.28), inset 0 -4px 8px rgba(0,0,0,0.55), 0 0 0 1.5px #7fb8f0, 0 0 0 3px rgba(6,14,30,0.5), ' +
    (compact ? '0 1px 2px rgba(0,0,0,0.4)' : '0 3px 8px rgba(0,0,0,0.45)');
  return (
    <div
      className={`${styles.medallion} ${dim ? styles.dim : ''}`}
      style={{ width: size, height: size }}
      aria-hidden='true'>
      {!compact && (
        <div
          className={styles.halo}
          style={{
            inset: -Math.round(size * 0.3),
            background: 'radial-gradient(closest-side, rgba(120,190,255,0.6), rgba(0,0,0,0))',
            opacity: 0.6
          }}
        />
      )}
      <div
        className={styles.disc}
        style={{
          background: 'radial-gradient(circle at 50% 28%, #1f4474 0%, #0f2444 58%, #08142a 100%)',
          boxShadow: shadow
        }}>
        <svg className={styles.svg} viewBox='0 0 48 48'>
          <defs>
            <linearGradient id='fzArmG' x1='0' y1='0' x2='1' y2='1'>
              <stop offset='0%' stopColor='#ffffff' />
              <stop offset='42%' stopColor='#bfe3ff' />
              <stop offset='100%' stopColor='#3a7fd0' />
            </linearGradient>
            <radialGradient id='fzCoreG' cx='40%' cy='35%' r='65%'>
              <stop offset='0%' stopColor='#ffffff' />
              <stop offset='55%' stopColor='#d3ecff' />
              <stop offset='100%' stopColor='#4a90e0' />
            </radialGradient>
            <g id='fzArm'>
              <path d='M24 5.2 L26.8 9.1 L25.7 10.3 L25.7 22.4 L22.3 22.4 L22.3 10.3 L21.2 9.1 Z' />
              <path d='M22.3 13 L17.8 10.1 L19.1 13.5 L22.3 15.3 Z' />
              <path d='M25.7 13 L30.2 10.1 L28.9 13.5 L25.7 15.3 Z' />
              <path d='M22.3 17.9 L19.1 16 L19.9 18.7 L22.3 20.1 Z' />
              <path d='M25.7 17.9 L28.9 16 L28.1 18.7 L25.7 20.1 Z' />
            </g>
          </defs>
          <g
            fill='url(#fzArmG)'
            stroke='rgba(8,26,54,0.5)'
            strokeWidth='0.6'
            strokeLinejoin='round'>
            {[0, 60, 120, 180, 240, 300].map((deg) => (
              <use key={deg} href='#fzArm' transform={deg ? `rotate(${deg} 24 24)` : undefined} />
            ))}
          </g>
          <path
            d='M24 19.3 L28.2 21.7 L28.2 26.3 L24 28.7 L19.8 26.3 L19.8 21.7 Z'
            fill='url(#fzCoreG)'
            stroke='rgba(8,26,54,0.55)'
            strokeWidth='0.6'
            strokeLinejoin='round'
          />
          <path
            d='M20.8 22.3 L24 20.5 L27.2 22.3'
            fill='none'
            stroke='rgba(255,255,255,0.9)'
            strokeWidth='0.9'
            strokeLinecap='round'
          />
        </svg>
        <div
          className={styles.glass}
          style={{
            background:
              'linear-gradient(180deg, rgba(230,245,255,0.2) 0%, rgba(230,245,255,0) 100%)'
          }}
        />
      </div>
    </div>
  );
}

export default memo(Freeze);
